import * as api from "@/api";
import Analytics from "@/components/analytics";
import BlockedPage from "@/components/blocked-page";
import CaptchaIframe from "@/components/captcha-iframe";
import CommonShell from "@/components/common-shell";
import LoadingScreen from "@/components/loading-screen";
import LoginPage from "@/components/login-page";
import MessageDialog from "@/components/message-dialog";
import OrdersPage from "@/components/orders-page";
import PremiumPage from "@/components/premium-page";
import ProfilePage from "@/components/profile-page";
import Prompt from "@/components/prompt";
import SecurityPage from "@/components/security-page";
import { loadDependencies } from "@/dependencies";
import { globals } from "@/globals";
import * as signals from "@/signals";
import { LoginInfo, LoginTab } from "@/types";
import {
	changePhone,
	deleteAccount,
	loadJs,
	logOut,
	showDialog,
} from "@/utils";
import { useQueryClient } from "@tanstack/react-query";
import { JSX, useCallback, useEffect } from "react";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";

function App(): JSX.Element {
	const location = useLocation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const isBlockedPage = location.pathname === "/blocked";
	const isLoginPage = location.pathname === "/login";

	const prefetch = useCallback((): void => {
		const handleForbiddenError = (): void => {
			void logOut();
		};
		void queryClient.prefetchQuery({
			queryFn: () => api.getAllDevices(handleForbiddenError),
			queryKey: ["devices", signals.login.value.username],
			retry: false,
		});
		void queryClient.prefetchQuery({
			queryFn: () => api.getExpirationTime(handleForbiddenError),
			queryKey: ["expirationTime", signals.login.value.username],
			retry: false,
		});
		void queryClient.prefetchQuery({
			queryFn: () => api.getPremiumInfo(handleForbiddenError),
			queryKey: ["premiumInfo", signals.login.value.username],
			retry: false,
		});
		void queryClient.prefetchQuery({
			queryFn: () => api.getProfile(["regTime"], handleForbiddenError),
			queryKey: ["profile", signals.login.value.username],
			retry: false,
		});
		void queryClient.prefetchQuery({
			queryFn: api.getOrders,
			queryKey: ["orders", signals.login.value.username],
			retry: false,
		});
	}, [queryClient]);

	const loggedIn = useCallback((): void => {
		const actionParam = globals.params.action;
		switch (actionParam) {
			case "changePhone": {
				void changePhone();
				break;
			}
			case "deleteAccount": {
				void deleteAccount();
				break;
			}
			default:
				break;
		}
		prefetch();
	}, [prefetch]);

	const getInit = useCallback(async (): Promise<void> => {
		try {
			const data = await api.getInit();
			signals.isLoadingScreenShown.value = false;
			signals.dynamicInfo.value = {
				...data,
				isLoaded: true,
			};
			if (data.backend) {
				globals.backend = data.backend;
			}
			if (
				process.env.NODE_ENV === "development" ||
				globals.isIosApp ||
				!data.region ||
				data.region === "CN"
			) {
				void loadJs("https://turing.captcha.qcloud.com/TCaptcha.js");
			}
			if (!isLoginPage) {
				if (data.login) {
					try {
						signals.login.value =
							typeof data.login === "string"
								? (JSON.parse(data.login) as LoginInfo)
								: data.login;
						loggedIn();
					} catch (error) {
						console.error(error);
					}
				} else {
					signals.isLoadingScreenShown.value = false;
					if (!isBlockedPage) {
						void navigate("/login", { replace: true });
					}
				}
			}
			if (data.blockReason) {
				signals.isLoadingScreenShown.value = false;
				if (!isBlockedPage) {
					void navigate("/blocked", {
						replace: true,
						state: { reason: data.blockReason },
					});
				}
				return;
			}
			if (data.redirectTo) {
				window.location.href = data.redirectTo;
				return;
			}
			if (data.alert) {
				void showDialog(data.alert);
			}
		} catch (error) {
			api.handleApiError(error);
			signals.isLoadingScreenShown.value = false;
		}
	}, [isBlockedPage, isLoginPage, loggedIn, navigate]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.ctrlKey || event.metaKey) {
				switch (event.code) {
					case "KeyI":
					case "KeyU": {
						if (process.env.NODE_ENV === "production") {
							event.preventDefault();
						}
						break;
					}
					default:
						break;
				}
			} else if (
				event.key === "F12" &&
				process.env.NODE_ENV === "production"
			) {
				event.preventDefault();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return (): void => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, []);

	useEffect(() => {
		const handleMessage = (event: MessageEvent): void => {
			try {
				if (typeof event.data !== "string") {
					return;
				}
				const data = JSON.parse(
					decodeURIComponent(window.atob(event.data)),
				) as { action?: string; validate?: string };
				if (data.action === "captcha") {
					signals.isCaptchaShown.value = false;
					if (globals.captchaCallback) {
						globals.captchaCallback({ ticket: data.validate });
						globals.captchaCallback = null;
					}
				}
			} catch (error) {
				if (process.env.NODE_ENV === "development") {
					console.warn(event.data, error);
				}
			}
		};
		window.addEventListener("message", handleMessage);
		return (): void => {
			window.removeEventListener("message", handleMessage);
		};
	}, []);

	useEffect(() => {
		const handleLocation = (): boolean => {
			if (
				process.env.NODE_ENV === "production" &&
				!window.location.hostname.endsWith(".retiehe.com")
			) {
				window.location.href = "https://account.retiehe.com/";
				return false;
			} else if (window.location.search) {
				window.history.replaceState(null, "", window.location.pathname);
			}
			return true;
		};

		if (signals.dynamicInfo.value.isLoaded || !handleLocation()) {
			return;
		}
		void getInit();
	}, [getInit, isBlockedPage, isLoginPage, loggedIn, navigate]);

	useEffect(loadDependencies, []);

	return (
		<>
			<Routes>
				<Route
					index
					element={
						<CommonShell>
							<ProfilePage />
						</CommonShell>
					}
				/>
				<Route
					path="/blocked"
					element={<BlockedPage loggedIn={loggedIn} />}
				/>
				<Route
					path="/login"
					element={<LoginPage loggedIn={loggedIn} />}
				/>
				<Route
					path="/login.html"
					element={<Navigate to="/login" />}
				/>
				<Route
					path="/login-airportal.html"
					element={<Navigate to="/login" />}
				/>
				<Route
					path="/orders"
					element={
						<CommonShell>
							<OrdersPage />
						</CommonShell>
					}
				/>
				<Route
					path="/premium"
					element={
						<CommonShell>
							<PremiumPage />
						</CommonShell>
					}
				/>
				<Route
					path="/security"
					element={
						<CommonShell>
							<SecurityPage />
						</CommonShell>
					}
				/>
				<Route
					path="/.well-known/change-password"
					element={
						<LoginPage
							initialTab={LoginTab.RESET_PASSWORD}
							loggedIn={loggedIn}
						/>
					}
				/>
				<Route
					path="*"
					element={<Navigate to="/" />}
				/>
			</Routes>
			{signals.isCaptchaShown.value && <CaptchaIframe />}
			{signals.isLoadingScreenShown.value && <LoadingScreen />}
			<MessageDialog />
			<Prompt />
			<Analytics />
		</>
	);
}

export default App;
